import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, Building, Sparkles, GraduationCap, Download, BookOpen } from "lucide-react";
import { colors, educationProgram } from "../config/site";

/**
 * Two-audience routing section. Shows up on the Home page just below the Hero.
 * Helps visitors self-select within 10 seconds: "I'm a family" vs "I'm a partner org".
 */
const AudienceSplit = () => {
  return (
    <section
      className="px-4 py-16 lg:py-20"
      style={{ backgroundColor: colors.cream }}
      data-testid="audience-split-section"
    >
      <div className="container mx-auto max-w-7xl">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p
            className="text-xs uppercase tracking-widest font-bold mb-2"
            style={{ color: colors.terracotta }}
          >
            Who We Serve
          </p>
          <h2 className="text-3xl lg:text-4xl font-bold" style={{ color: colors.charcoal }}>
            Find the path that fits you.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {/* For Families */}
          <Link
            to="/resources"
            className="group rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-all"
            data-testid="audience-card-families"
          >
            <div
              className="h-full p-8 lg:p-10 flex flex-col"
              style={{ backgroundColor: colors.white, borderLeft: `5px solid ${colors.sage}` }}
            >
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                style={{ backgroundColor: colors.sage + "22" }}
              >
                <Users className="w-7 h-7" style={{ color: colors.primary }} />
              </div>
              <div
                className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3 w-fit"
                style={{ backgroundColor: colors.sage + "22", color: colors.primary }}
              >
                For Renters &amp; Families
              </div>
              <h3 className="text-2xl font-bold mb-3" style={{ color: colors.charcoal }}>
                Education to help you stay housed.
              </h3>
              <p className="text-sm lg:text-base mb-6 flex-grow" style={{ color: colors.textMuted }}>
                {educationProgram.forFamiliesNote} Take a quiz, download a guide, or read up on
                renter rights.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm" style={{ color: colors.charcoal }}>
                  <Sparkles className="w-4 h-4" style={{ color: colors.terracotta }} />
                  Rent Readiness Quiz &amp; Lease Detective
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: colors.charcoal }}>
                  <BookOpen className="w-4 h-4" style={{ color: colors.terracotta }} />
                  Real-life case studies &amp; decision-making simulations
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: colors.charcoal }}>
                  <Download className="w-4 h-4" style={{ color: colors.terracotta }} />
                  Move-In Checklist, Budget Worksheet, Maintenance Guide
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: colors.charcoal }}>
                  <GraduationCap className="w-4 h-4" style={{ color: colors.terracotta }} />
                  Workshops on lease literacy &amp; credit building
                </li>
              </ul>
              <span
                className="inline-flex items-center gap-2 text-sm font-bold group-hover:gap-3 transition-all"
                style={{ color: colors.primary }}
              >
                Browse Resources <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>

          {/* For Partner Organizations */}
          <Link
            to="/partnerships"
            className="group rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-all"
            data-testid="audience-card-partners"
          >
            <div
              className="h-full p-8 lg:p-10 flex flex-col text-white"
              style={{
                background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
              }}
            >
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                style={{ backgroundColor: "rgba(255,255,255,0.15)" }}
              >
                <Building className="w-7 h-7" style={{ color: colors.gold }} />
              </div>
              <div
                className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3 w-fit"
                style={{ backgroundColor: "rgba(255,255,255,0.15)", color: colors.gold }}
              >
                For Partner Organizations
              </div>
              <h3 className="text-2xl font-bold mb-3">
                Bring our workshops to the families you serve.
              </h3>
              <p className="text-sm lg:text-base mb-6 flex-grow opacity-90">
                {educationProgram.forPartnersNote}
              </p>
              <ul className="space-y-2 mb-6 opacity-95">
                <li className="flex items-center gap-2 text-sm">
                  <Sparkles className="w-4 h-4" style={{ color: colors.gold }} />
                  Customized curriculum for your client base
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <GraduationCap className="w-4 h-4" style={{ color: colors.gold }} />
                  Live training sessions, in-person or virtual
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4" style={{ color: colors.gold }} />
                  Tenant success follow-up &amp; support
                </li>
              </ul>
              <span className="inline-flex items-center gap-2 text-sm font-bold group-hover:gap-3 transition-all">
                See Partnership Details <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default AudienceSplit;
